import React, { useState, useCallback } from "react";
import { Input, Button, AutoComplete, DatePicker, Switch, TimePicker, Select, Checkbox, Typography, Tooltip } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
const { Search } = Input;

const CopySuffix = ({ value }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback((e) => {
        e.stopPropagation();
        const text = value != null ? String(value) : "";
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }, [value]);
    return (
        <Tooltip title={copied ? "Copied!" : "Copy"}>
            <span
                onClick={handleCopy}
                style={{ cursor: value ? "pointer" : "default", color: copied ? "#52c41a" : "#aaa", fontSize: 12 }}
            >
                {copied ? <CheckOutlined /> : <CopyOutlined />}
            </span>
        </Tooltip>
    );
};
const capitalizeFirstLetter = (label) => {
    return label
        ?.split(" " || '/')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};
 
export const InputField = ({
    label,
    type,
    name,
    formcontrol,
    value,
    onChange,
    error,
    placeholder,
    max,
    min,
    onInput,
    id,
    style,
    allowClear,
    onSearch,
    onBlur,
    onFocus,
    onClear,
    enterButton,
    required,
    disabled,
    inputMode,
    pattern,
    addonAfter,
    showCount,
    maxLength,
    prefix,
    suffix,
    size,
    copyable,
}) => {
    const InputComponent = onSearch ? Search : Input;
    return (
        <>
            {label && (
                <label className="col-form-label" style={{ fontSize: "12px" }}>
                    {capitalizeFirstLetter(label)}
                    {required && <span className="text-danger">*</span>}
                </label>
            )}{label && <br />}
            <InputComponent
                type={type}
                name={name}
                className={`${formcontrol || ''} ${error ? 'is-invalid' : ''}`} // Ensure className is always a string
                value={value ?? undefined}
                onChange={onChange}
                placeholder={placeholder}
                max={max}
                min={min}
                onInput={onInput}
                style={{ fontSize: "12px", ...(style || {}) }}   // ⬅ Here we fixed 12px font size
                // onSearch={onSearch}
                {...(onSearch ? { onSearch } : {})} // Pass onSearch only if it exists
                allowClear={allowClear}
                onBlur={onBlur}
                onClear={onClear}
                onFocus={onFocus}
                disabled={disabled}
                id={id}
                enterbutton={onSearch ? enterButton : undefined}
                status={error ? "error" : undefined}
                inputMode={inputMode}
                pattern={pattern}
                addonAfter={addonAfter}
                showCount={showCount}
                maxLength={maxLength}
                prefix={prefix}
                suffix={copyable ? <CopySuffix value={value} /> : suffix}
                size={size}
            />
            {error ? <small className="text-danger">{error}</small> : null}
        </>
    );
};
 
export const SelectInput = ({
    label,
    options,
    allowClear,
    onClear,
    value,
    formControl,
    required,
    onChange,
    error,
    name,
    style,
    placeholder,
    dropdownStyle,
    filterOption,
    onSelect,
    size,
    disabled,
    showSearch,
    mode,
    menuPlacement,
    dropdownRender,
    optionLabelProp,
      onSearch,
    searchValue,
    autoClearSearchValue,
    onDropdownVisibleChange,
}) => {
    return (
        <div className="input-block mb-3">
            {label &&
                (
                    <label className="col-form-label" style={{ fontSize: "12px" }}>
                        {capitalizeFirstLetter(label)}{required && <span className="text-danger">*</span>}
                    </label>
                )
            }{label && <br />}
            <Select
                showSearch
                name={name}
                options={options}
                value={value ?? undefined}
                onChange={onChange}
                onSearch={onSearch}               
                searchValue={searchValue}          
                autoClearSearchValue={autoClearSearchValue}  
                onDropdownVisibleChange={onDropdownVisibleChange}
                style={{ width: "100%", ...(style || {}) }}
                dropdownStyle={{ ...(dropdownStyle || {}), fontSize: "12px" }}
                required={required}
                placeholder={placeholder}
                // dropdownStyle={dropdownStyle}
              filterOption={(input, option) => {
    const label = String(option?.label ?? option?.value ?? "");
    return label.toLowerCase().includes(input.toLowerCase());
}}


                onSelect={onSelect}
                // className={`${formControl ? 'w-100' : ''} ${error ? 'is-invalid' : ''}`}
                className={`${formControl ? 'w-100' : ''} ${error ? 'is-invalid' : ''} select-font-12 fixed-select-height`} // ⬅ added class
                size={size}
                disabled={disabled}
                allowClear={allowClear}
                onClear={onClear}
                mode={mode}
                menuplacement={menuPlacement}
                popupClassName="select-font-12-dropdown" 
                dropdownRender={dropdownRender}
                optionLabelProp={optionLabelProp}
            />
            {error ? <small className="text-danger">{error}</small> : null}
        </div>
 
    );
};
export const SelectInputWithoutSearch = ({
    label,
    options,
    allowClear,
    onClear,
    value,
    formControl,
    required,
    onChange,
    error,
    name,
    style,
    placeholder,
    filterOption,
    onSelect,
 
    size,
    disabled,
    mode,
 
}) => {
    return (
        <div className="input-block mb-3">
            {label &&
                (
                    <label className="col-form-label">
                        {capitalizeFirstLetter(label)}{required && <span className="text-danger">*</span>}
                    </label>
                )
            }{label && <br />}
            <Select
                mode={mode}
                name={name}
                options={options}
                value={options.find(option => option.value === value) ? value : undefined}
                onChange={onChange}
                style={style}
                placeholder={placeholder}
                filterOption={filterOption}
                onSelect={onSelect}
                className={`${formControl ? 'w-100' : ''} ${error ? 'is-invalid' : ''}`}
                size={size}
                disabled={disabled}
                allowClear={allowClear}
                onClear={onClear}
            />
            {error ? <small className="text-danger">{error}</small> : null}
        </div>
 
    );
};
 
export const SwitchField = ({
    label,
    name,
    checked,
    onChange,
    disabled
}) => {
    return (
        <div className="input-block mb-3">
            {label && <label className="col-form-label" style={{ fontSize: "12px" }}>  {capitalizeFirstLetter(label)}</label>}{label && <br />}
            <Switch
                name={name}
                checked={checked}
                onChange={onChange}
                disabled={disabled}
            />
        </div>
    );
};
 
export const TextareaField = ({ label, name, id, value, placeholder, onChange, rows, onBlur, disabled, onFocus, size, style,autoSize }) => {
    return (
        <div className="input-block mb-3">
            <label className="col-form-label" style={{ fontSize: "12px" }}>{label}</label>
            <TextArea
                name={name}
                id={id}
                value={value}
                placeholder={placeholder}
                onChange={onChange}
                rows={rows}
                onBlur={onBlur}
                onFocus={onFocus}
                disabled={disabled}
                size={size}
                style={{ fontSize: "12px", ...(style || {}) }}   // ⬅ Here we fixed 12px font size
                autoSize={autoSize}
            />
        </div>
    );
};
 
export const CheckboxField = ({ label, name, onChange, checked, style }) => {
    return (
        <div className="input-block mb-0">
 
            <Checkbox
                name={name}
                onChange={onChange}
                checked={checked}
                style={style}
            />
            <Typography.Text style={{ marginLeft: "5px", fontSize: '15px', color: '#000' }}>{label}</Typography.Text>
        </div>
    );
};
 
export const DatePickerField = ({
    label,
    name,
    onChange,
    showTime,
    value,
    format,
    disabledDate,
    placeholder,
    style,
    onKeyDown,
    inputReadOnly,
    allowClear,
    required,
    error,
    max,
    selected,
    filterDate,
    minDate,
    disabled,
    type,
    formcontrol,
    getPopupContainer,
    onFocus,
    renderExtraFooter,
    panelRender,
}) => {
    return (
        <div className="input-block mb-3">
            {label && <label className="col-form-label" style={{ fontSize: "12px" }}>{capitalizeFirstLetter(String(label))}{required && <span className="text-danger">*</span>}</label>}{label && <br />}
            <DatePicker
                showTime={showTime}
                name={name}
                value={value ?? undefined}
                format={format}
                disabledDate={disabledDate}
                onChange={onChange}
                allowClear={allowClear}
                placeholder={placeholder}
                className={`${formcontrol}${error ? 'is-invalid w-100' : ''}`}
                style={style}
                onKeyDown={onKeyDown}
                inputReadOnly={inputReadOnly}
                max={max}
                minDate={minDate}
                selected={selected}
                filterDate={filterDate}
                disabled={disabled}
                type={type}
                getPopupContainer={getPopupContainer} // Prevents form from moving
                onFocus={onFocus} // Stops auto-scroll on focus
                autoFocus={false} // Ensures it doesn’t move focus unexpectedly
                renderExtraFooter={renderExtraFooter}
                panelRender={panelRender}
                showToday={false}
            />
            {error ? <small className="text-danger">{error}</small> : null}
        </div>
    )
}
 
 
export const AntdButton = ({
    label,
    onClick,
    icon,
    type,
    disabled,
    style,
    variant,
    color,
    size,
    className,
    htmlType,
    block,
    loading
}) => {
    const buttonRef = React.useRef(null)
    return (
 
        <Button
            ref={buttonRef}
            size={size}
            type={type}
            icon={icon}
            onClick={onClick}
            disabled={disabled}
            style={{ fontSize: "12px", ...(style || {}) }}   // ⬅ Here we fixed 12px font size
            variant={variant}
            color={color}
            className={className}
            htmlType={htmlType}
            block={block}
            loading={loading}
        >
            {label}
        </Button>
 
    );
};
 
export const AutoCompleteField = ({
    label,
    value,
    options,
    onChange,
    onSearch,
    placeholder,
    allowClear = true,
    style,
}) => {
    return (
        <div className="input-block mb-3">
            <label className="col-form-label">{capitalizeFirstLetter(label)}</label>
            <AutoComplete
                value={value}
                options={options}
                onChange={onChange}
                onSearch={onSearch}
                placeholder={placeholder}
                allowClear={allowClear}
                style={style}
            >
                <Input onChange={onSearch} />
            </AutoComplete>
        </div>
    );
};
export const TimePickerField = ({
    label,
    name,
    value,
    onChange,
    format,
    allowClear,
    inputReadOnly,
    style,
}) => {
    return (
        <div className="input-block mb-3">
            {label && <label className="col-form-label">{capitalizeFirstLetter(label)}</label>}{label && <br />}
            <TimePicker
                inputReadOnly={inputReadOnly}
                format={format}
                allowClear={allowClear}
                name={name}
                value={value}
                onChange={onChange}
                style={style}
            />
        </div>
    );
};